/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { NodeDefinitionService } from '../services/NodeDefinitionService';

export interface Node {
  id: string;
  name: string;
  description: string | null;
  type: string;
  category: string | null;
  logoUrl: string | null;
  settings: any | null;
  inputSchema: any | null;
  outputSchema: any | null;
  createdAt: string;
}

export interface NodesCategory {
  category: string | null;
  nodes: Node[];
}

export const useNodes = () => {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;
    
    const fetchNodes = async () => {
      setLoading(true);

      try {
        // Utilizando o NodeDefinitionService para buscar todos os nós
        const { data, error } = await NodeDefinitionService.getAll();

        if (error) throw error;

        if (mounted && data) {
          // Garantir que os dados estão no formato correto do tipo Node
          const typedNodes = data.map(node => ({
            id: String(node.id),
            name: node.name as string,
            description: node.description as string | null,
            type: node.type as string,
            category: node.category as string | null,
            logoUrl: node.logo_url as string | null,
            settings: node.settings as any,
            inputSchema: node.input_schema as any,
            outputSchema: node.output_schema as any,
            createdAt: node.created_at as string
          } satisfies Node));

          setNodes(typedNodes);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          console.error('Error fetching nodes:', err);
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchNodes();

    return () => {
      mounted = false;
    };
  }, []);

  // Organiza os nodes por categoria
  const categorizedNodes = (): NodesCategory[] => {
    const categories: { [key: string]: Node[] } = {};
    
    nodes.forEach(node => {
      // Garantir que a categoria nunca seja null para uso como chave em categories
      const category = node.category || 'Sem Categoria';
      
      if (category !== "Files") {
        if (!categories[category]) {
          categories[category] = [];
        }
        categories[category].push(node);
      }
    });

    return Object.entries(categories)
      .map(([category, nodes]) => ({
        // Se a categoria original era "Sem Categoria", restaura para null para compatibilidade
        category: category === 'Sem Categoria' ? null : category,
        nodes: nodes.sort((a, b) => a.name.localeCompare(b.name))
      }))
      .sort((a, b) => {
        // Garante que "Core" aparece primeiro
        if (a.category === "Core") return -1;
        if (b.category === "Core") return 1;
        
        // Depois "Data"
        if (a.category === "Data") return -1;
        if (b.category === "Data") return 1;
        
        // Depois "Text" 
        if (a.category === "Text") return -1;
        if (b.category === "Text") return 1;
        
        // Por último "Logic"
        if (a.category === "Logic") return -1;
        if (b.category === "Logic") return 1;
        
        // Mantém a ordem alfabética para as outras categorias
        return (a.category || '').localeCompare(b.category || '');
      });
  };

  return {
    nodes: categorizedNodes(),
    loading,
    error
  };
};
